import moment from 'moment-timezone';
import { ORDER_STATUSES } from 'utils/order/constants';
import numeral from 'numeral';

const getRecurringLoads = (order, stop) => {
  return stop.loads
    ? [...stop.loads]
    : order?.shipments?.reduce(
        (current, shipment) => [...current, ...(shipment.loads || [])],
        []
      );
};

export const getLoads = (order, stop, isRecurring) => {
  return isRecurring
    ? getRecurringLoads(order, stop)
    : [
        ...(stop.load_pickup || []),
        ...(stop.load_dropoff || []),
        ...(stop.loads || []),
      ]; // this could be good if the BE returns it with the same structure for both cases
};

export const getTimeInfo = (stop, summaryInProgress, summaryDone) => ({
  valueFrom: summaryInProgress || stop.time_from,
  valueTo: summaryDone || stop.time_to,
  timezone: stop.time_from_tz,
});

export const getIsCompleted = (stop) =>
  stop.summary?.in_progress && stop.summary?.done && !stop.store;

export const getIsOnWay = (summaryOnRoute, summaryInProgress, summaryDone) =>
  !!summaryOnRoute && !summaryInProgress && !summaryDone;

export const getIsLoadingOrUnloading = (summaryInProgress, summaryDone) =>
  !!summaryInProgress && !summaryDone;

export const getArrivalTime = (stop) => {
  const at = stop?.summary?.in_progress || stop.time_from;
  return !!at ? moment(at).tz(stop.address.timezone)?.format('HH:mm') : null;
};

export const getMaxHeight = (loads) =>
  loads.reduce(
    (maxValue, load) =>
      load.item_height >= maxValue ? load.item_height : maxValue,
    0
  );

export const getTotalWeight = (loads) =>
  loads.reduce((totalValue, load) => totalValue + load.total_weight, 0);

export const getStopStatusLabel = ({
  stopType,
  isDone,
  isPossibleStandstill,
  isStandstill,
  isOnWay,
  isLoadingOrUnloading,
}) => {
  if (isDone) {
    return { value: `statuses.${stopType}.done` };
  }
  if (isPossibleStandstill) {
    return {
      value: `statuses.${stopType}.possible_standstill`,
      style: 'warning',
      hasStandstillOrPossibility: true,
    };
  }
  if (isStandstill) {
    return {
      value: `statuses.${stopType}.standstill`,
      style: 'warning',
      hasStandstillOrPossibility: true,
    };
  }
  if (isOnWay) {
    return { value: `statuses.${stopType}.on_way` };
  }
  if (isLoadingOrUnloading) {
    return { value: `statuses.${stopType}.on_progress` };
  }
  return { value: `${stopType}` };
};

export const getStandstillType = (arrivalTime, stop) =>
  arrivalTime && moment(arrivalTime).isBefore(moment(stop.time_from))
    ? 'standstill-early'
    : 'standstill-after';

const STANDSTILL_LABEL_BASE = 'label.incidents.standstill.';
export const getStandstillLabelFactory =
  ({
    isInProgress,
    isStandstill,
    isPossibleStandstill,
    stopType,
    isDone,
    order,
    t,
    getStandstillLabelParams,
  }) =>
  (standstillConfig) => {
    const params = getStandstillLabelParams(standstillConfig);
    if (!params) {
      return null;
    }
    if (isInProgress) {
      if (isStandstill) {
        return t(
          `${STANDSTILL_LABEL_BASE}${stopType}_confirmed_standstill`,
          params
        );
      }
      if (isPossibleStandstill) {
        return t(
          `${STANDSTILL_LABEL_BASE}${stopType}_possible_standstill`,
          params
        );
      }
    }
    if (isPossibleStandstill || isStandstill) {
      if (isDone && order?.status === ORDER_STATUSES.IN_TRANSIT) {
        return t(`${STANDSTILL_LABEL_BASE}job_in_transit`, params);
      }
      if (isDone) {
        return t(`${STANDSTILL_LABEL_BASE}job_done`);
      }
    }
    return null;
  };

export const getStandstillLabelParamsFactory =
  ({ valueFrom, timezone, summaryDone, arrivalTime, t }) =>
  (standstillConfig) => {
    const maxWaitingTime =
      standstillConfig?.max_time || standstillConfig?.load_or_unload_time || 0;

    if (!standstillConfig || maxWaitingTime === 0) {
      return null;
    }

    const costPerHour = numeral(standstillConfig.cost_per_hour).format(
      t('LiteralCurrency:format')
    );
    const startTime = moment(valueFrom).tz(timezone).format('HH:mm');
    const startStandstillTime = moment(valueFrom)
      .add(maxWaitingTime, 'minutes')
      .tz(timezone);

    const standstillTime = summaryDone
      ? moment.duration(moment(summaryDone).diff(startStandstillTime)).minutes()
      : null;

    return {
      costPerHour,
      arrivalTime,
      startTime,
      leavingTime: summaryDone,
      startStandstillTime: startStandstillTime.format('HH:mm'),
      maxWaitingTime,
      standstillTime,
    };
  };
